import { Request } from 'express';

/* Types */
export type NewOidcToSamlRequest = Request & { query: { state: string } };

export enum AuthorizationStatusType {
    Authorized = 'Authorized',
    Unauthorized = 'Unauthorized',
    EpiMismatchError = 'EPI-Mismatch-Error',
    MyChartMismatchError = 'MyChart-Mismatch-Error',
    UnknownError = 'Unknown-Error',
}

enum GetAuthorizationResponseType {
    Success,
    CannotGetJWT,
    InvalidToken,
    UnknownError,
}

// https://learn.microsoft.com/en-us/azure/active-directory-b2c/error-codes
// Maintains a list of error codes which we wish to direct the user to an error page. Previously, the flow does a loop back to the sign-in page
export const AZUREB2C_ERROR_CODES = ['AADB2C90161', 'AADB2C90157', 'AADB2C90159', 'AADB2C90013', 'AADB2C90117'];

// TODO: Use PIMS cache instead of passing this stuff around
type GetAuthorizationRequestBase = {
    details: {
        instantActivationCode: string;
        isNonPatientProxy: boolean;
    };
};

type GetAuthorizationSuccess = GetAuthorizationRequestBase & {
    status: GetAuthorizationResponseType.Success;
    authorizationStatus: AuthorizationStatusType;
};

type GetAuthorizationFailure = GetAuthorizationRequestBase & {
    status: GetAuthorizationResponseType.InvalidToken | GetAuthorizationResponseType.UnknownError | GetAuthorizationResponseType.CannotGetJWT;
};

export type GetAuthorizationResponse = GetAuthorizationSuccess | GetAuthorizationFailure;

export type PimsGetAuthorizationResponse = {
    aiaCodeAuthorizationStatus: AuthorizationStatusType;
};

export type PimsPostVerificationSessionResponse = {
    verificationSessionId: string;
    sessionTokenId: string;
};

export type PimsPostVerificationSessionRequest = {
    jwt: string;
    pimsApiUrl: string;
    brand: string;
    ehrSystem: string;
    deviceType: string;
    redirectUri: string;
    customScheme?: string;
    vendor: string;
    language: string;
};

export enum SessionStatus {
    NONE = 'No Session Found',
    IN_PROGRESS = 'Existing Session In Progress',
    COMPLETED = 'Session Completed',
    FAILED = 'Request Failed',
}

export type PimsGetVerificationSessionResponse = {
    status: SessionStatus;
    redirectUrl?: string;
    token?: string;
};

export type PimsErrorResponse = {
    code: string;
    message: string;
    name: string;
};

export type PimsAuthorizationResponse = {
    healthSystemId: string;
    authorizedSystem: string;
};

export type PimsActivateMyChartResponse = {
    wasSuccessful: boolean;
    error?: string;
};

/*
 * AIA /getEmail response
 */

type GetEmailResponseFailure = {
    error: string;
};

type GetEmailResponseSuccess = {
    email: string;
    isNonPatientProxy: boolean;
    isTokenValid: boolean;
    analyticsProperties: {
        activation_method: string;
        activation_source: string;
        link_expired: string;
        epi_authorized: string;
    };
};

export type GetEmailResponse = GetEmailResponseSuccess | GetEmailResponseFailure;

/**
 * AIA /selectEmail request
 */

export type SelectEmailRequest = Request<
    any,
    any,
    {
        brand: string;
        ehrSystem: string;
        email: string;
        isExpired: boolean;
        isNonPatientProxy: boolean;
        token: string;
        ehrInstance: string;
        deviceId?: string;
        sessionId?: number;
    }
>;

/**
 * AIA /selectEmail response
 */

export type SelectEmailResponse = {
    /**
     * Local protected PIP route to load after MSAL finishes interactive auth.
     */
    redirectPath: string;

    /**
     * Query parameters for the protected route and the Azure B2C authorize request.
     */
    redirectParams: Record<string, string>;
};

export type MyChartLoginRequest = {
    username: string;
    password: string;
};
